// nodeBuilderUtils.js
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import { Tooltip } from '@mantine/core';
import { Bluetooth, Network } from 'tabler-icons-react';
import NavigNode from '../../node/NavigNode';
import { getBandsPosition } from '../../Utils/LayoutUtils';

import { formatSmartTimestamp } from '../../../utils/date';

import {
  calculateLeftVal,
  calculateNodeLeftContainer,
} from '../../../utils/nodeLayout';
import { NODE_NAVIG_WIDTH } from '../../../constants/layout';
import { IconExpandColumn } from '../NavigHeaderBar/NavigHeaderBarCss';
import { DoubleArrowIcon } from '../../../constants/icon';
import DataColumns from '../DataColumns/DataColumns';
import { rgbStringToHex } from '../../Utils/Utils';
import { TooltipButton } from '../VerticalButtonBar';
import { getRangeHeaderWidth2 } from '../DataColumns/DataColumnsUtils';
import HeaderCardsContainer from '../NavigHeader/headerCardsContainer';

/**
 * Calcule les statistiques pour un nœud individuel
 * @param {Object} node - Nœud à analyser
 * @param {string} paramClassifNode - Paramètre de classification
 * @param {Map} dataMap - Map des statistiques
 * @param {Array} arrayStatBy - Statistiques à calculer
 */
const calculateNodeStats = (node, paramClassifNode, dataMap, arrayStatBy) => {
  if (!node || !paramClassifNode || !dataMap || !arrayStatBy) return;

  let mainEntry =
    paramClassifNode === 'default' ? 'default' : node.data[paramClassifNode];
  mainEntry += `_${node.level}`;

  if (!dataMap.get(mainEntry)) {
    dataMap.set(mainEntry, new Map());
  }

  const subMap = dataMap.get(mainEntry);
  const currentCount = subMap.get('Count') || 0;
  subMap.set('Count', currentCount + 1);

  const fieldStat = arrayStatBy[node.depth - 1];
  if (node.data[fieldStat]) {
    const currentSum = subMap.get('Sum') || 0;
    subMap.set('Sum', currentSum + node.data[fieldStat]);
    const average =
      Math.round((100 * subMap.get('Sum')) / (currentCount + 1)) / 100;
    subMap.set('Average', average);
  }
};

/**
 * Calcule les attributs visuels d'un nœud (couleur, etc.)
 * @param {Object} node - Nœud à traiter
 * @param {Function} colorScale - Échelle de couleurs à appliquer
 * @param {string} paramClassifNode - Paramètre de classification
 * @returns {Object} Attributs visuels du nœud
 */
const computeNodeVisuals = (
  node,
  colorScale,
  paramClassifNode,
  displayModeCol
) => {
  const rawValue = node?.data?.[paramClassifNode];

  if (
    displayModeCol !== 'card' ||
    !paramClassifNode ||
    paramClassifNode === 'default'
  ) {
    return {
      nodeColor: 'var(--ds-base)',
      notSet: false,
    };
  }

  if (!rawValue) {
    return {
      nodeColor: 'blinking',
      notSet: true,
    };
  }

  let nodeColor =
    typeof colorScale === 'function'
      ? colorScale(rawValue) ?? 'var(--ds-base)'
      : 'var(--ds-base)';

  return {
    nodeColor: rgbStringToHex(nodeColor),
    notSet: false,
  };
};

/**
 * Calcule les dimensions et la position d'un nœud
 * @param {Object} params - Paramètres de calcul
 * @returns {Object} Position et dimensions du nœud
 */
const computeNodeGeometry = ({
  node,
  currBand,
  indBand,
  displayMode,
  isMobile,
  widthScreen,
  fusedDataColumns,
  fusedLevelWidth,
}) => {
  let nodeWidth;
  switch (displayMode) {
    case 'grid':
      nodeWidth = currBand.width - 80;
      break;

    case 'card':
    case 'stackcard':
      nodeWidth = NODE_NAVIG_WIDTH;
      break;

    case 'stack':
      // nodeWidth = 2600; // exemple pour stacked, adapte la valeur si nécessaire
      //  nodeWidth = getRangeHeaderWidth2(fusedDataColumns[node.depth - 1]) + 200;
      nodeWidth = fusedLevelWidth?.[node.depth - 1] + 140 ?? 2600;
      break;

    default:
      nodeWidth = NODE_NAVIG_WIDTH; // fallback
  }
  const leftVal = calculateLeftVal({ currBand, indBand, node });

  return {
    width: isMobile ? widthScreen - 70 : nodeWidth,
    left: calculateNodeLeftContainer({
      displayMode,
      leftVal,
      level: node.level,
      isMobile,
    }),
    top: node.y,
    height: node.ySize,
  };
};

export const buildCardEnvelop = ({ nodes = [], displayMode }) => {
  let res = nodes.reduce((acc, currNode, index) => {
    // besoi d'une envellop ?
    const needEnvellop =
      currNode.depth > 1 &&
      (displayMode[currNode.depth - 1] === 'card' ||
        displayMode[currNode.depth - 1] === 'stackcard') &&
      displayMode[currNode.depth - 2];

    if (needEnvellop) {
      let parentKey = currNode?.parent?.data?.key;
      if (parentKey) {
        let envelop = acc.find((val) => val.id === parentKey);
        if (envelop) {
          envelop.ymin = Math.min(envelop.ymin, currNode.y);
          envelop.ymax = Math.max(envelop.ymax, currNode.y + currNode.ySize);
        } else {
          let newEntry = {};
          newEntry.id = parentKey;
          newEntry.ymin = currNode.y;
          newEntry.ymax = currNode.y + currNode.ySize;
          newEntry.depth = currNode.depth;
          acc.push(newEntry);
        }
      } else {
      }
    }
    return acc;
  }, []);

  return res;
};

/**
 * Crée les composants de nœuds pour le rendu
 * @param {Object} params - Paramètres pour la création des nœuds
 * @returns {Array} Composants de nœuds rendus
 */
export const buildNodes = ({
  nodes,
  dataMap,
  myNodeColorScale,
  displayMode,
  fusedDataColumns,
  isMobile,
  bands_config,
  arrayClassifBy,
  arrayStatBy,
  eventHandlers,
  draggedItem,
  updateNodeHeight,
  handleDrop,
  setTimeLineChangeCentral,
  fusedLevelWidth,
}) => {
  if (!nodes) return [];

  const widthScreen =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;

  return nodes.map((node, index) => {
    const { canWrite, canDuplicateType } = node.data;
    const paramClassifNode = arrayClassifBy[node.depth - 1];

    // Calcule les statistiques et les attributs visuels
    calculateNodeStats(node, paramClassifNode, dataMap, arrayStatBy);

    let visuals = computeNodeVisuals(
      node,
      myNodeColorScale[node.depth - 1],
      paramClassifNode,
      displayMode[node.depth - 1]
    );
    node.data.nodeColor = visuals.nodeColor;

    // Calcule la géométrie du nœud
    const currBand = getBandsPosition(node.depth - 1, bands_config);
    const isGrid = displayMode[node.depth - 1] === 'grid' && !isMobile;
    const isStack = displayMode[node.depth - 1] === 'stack';
    const isCard = displayMode[node.depth - 1] === 'card';
    const geometry = computeNodeGeometry({
      node,
      currBand,
      indBand: node.depth - 1,
      displayMode: displayMode[node.depth - 1],
      isMobile,
      widthScreen,
      fusedDataColumns,
      fusedLevelWidth,
    });

    // Vérifie si la colonne suivante a du contenu
    const isNextColumnEmpty = isEmpty(
      nodes.filter((a) => a.depth - 1 === node.depth - 1)
    );
    const left = visuals.notSet ? geometry.left - 5 : geometry.left;
    const isNodeOutsidePane =
      !isStack && (left < -20 || left > currBand.width - NODE_NAVIG_WIDTH);
    const top = geometry.top - (visuals.notSet ? 5 : 0);
    const finderLeft = left < -20 ? -12 : currBand.width - 80;

    const isFirstChild =
      node?.parent?.data?.children?.[0]?.objId === node.data.key;

    const firstChildCard =
      node?.parent?.data?.children?.[0]?.objId === node.data.key &&
      displayMode &&
      isCard;
    // console.log('%s  is firstChildCard %s', node.data.key, firstChildCard);
    return (
      <>
        {' '}
        {isFirstChild && false && (
          <div
            style={{
              position: 'absolute',
              left: left,
              top: top - 17,
              fontSize: 11,
              color: '#888888',
              zIndex: 20000,
            }}
          >
            {node.data.type}
          </div>
        )}
        {isNodeOutsidePane && (
          <div
            style={{
              zIndex: '20000',
              position: 'absolute',
              left: finderLeft,
              top: top + node.ySize / 2 - 6,
              '& :hover': {
                left: finderLeft < 0 ? finderLeft - 5 : finderLeft + 5,
              },
            }}
          >
            <TooltipButton
              withArrow
              position="top"
              multiline
              width={140}
              label={`${node.data.name}-${formatSmartTimestamp(
                node.data[paramClassifNode]
              )}`}
            >
              <div
                onClick={() =>
                  setTimeLineChangeCentral(
                    node.depth - 1,
                    node.data[paramClassifNode]
                  )
                }
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 24,
                  width: 24,
                  backgroundColor: 'var(--ds-bg-base)',
                  color: 'var(--ds-text-active)',
                  border: '1px solid var(--ds-border-base)',

                  borderRadius: '50%',
                }}
              >
                {finderLeft < 0 ? '\u25C0' : '\u25B6'}
              </div>
            </TooltipButton>
          </div>
        )}
        {/* {firstChildCard && (
          <HeaderCardsContainer
            level={node.depth - 1}
            x={left}
            y={top - 28}
            width={600}
          />
        )} */}
        <NavigNode
          key={node.data.key}
          canWrite={canWrite}
          canDuplicateType={canDuplicateType}
          dragging={draggedItem === node.data.key}
          draggingItem={draggedItem}
          height={geometry.height}
          isGrid={isGrid}
          displayMode={displayMode[node.depth - 1]}
          left={left}
          node={node}
          nodeColor={visuals.nodeColor}
          nodeLeft={0}
          top={top}
          width={geometry.width}
          updateNodeHeight={updateNodeHeight}
          isHadContend={isNextColumnEmpty}
          handleDrop={handleDrop}
          isNodeOnActivePanel={true}
          onTouchStart={eventHandlers.handleTouchStart}
          onTouchEnd={eventHandlers.handleTouchEnd}
          onDragEnd={eventHandlers.handleDragEnd}
          onDragStart={eventHandlers.handleDragStart}
          onDrop={eventHandlers.handleOnDrop}
          onMouseUp={eventHandlers.handleMouseUp}
          onMouseOver={eventHandlers.handleMouseOver}
          isNodeOutsidePane={!visuals.notSet && isNodeOutsidePane}
          notSet={visuals.notSet}
        >
          <IconExpandColumn>
            <TooltipButton
              position="left"
              openDelay={2000}
              label={t('app.expand')}
            >
              {' '}
              <DoubleArrowIcon fill={'var(--ds-icon-secondary)'} />
            </TooltipButton>
          </IconExpandColumn>
          <DataColumns node={node} />
        </NavigNode>
      </>
    );
  });
};
